import { EnergySystemType } from '../../../generated/graphql'
import { i18n } from '../../../i18n'
import { CalibrationChartType, CustomSeries } from './chart-util'
import {
  energyCapacityPerAreaUnit,
  formatHour,
  formatTemperature,
  FullFormatOptions,
  monthUnit,
  powerPerAreaUnit,
  temperatureUnit,
} from '../../../util/formatters'

export type GraphData = {
  id: number
  name: string
  measuredData: CustomSeries
  energySystemData: CustomSeries[]
  dependentEnergySystems: EnergySystemType[]
  chartType: CalibrationChartType
  xFormatter: (value: number) => string
  yFormatter: (value: number, options?: FullFormatOptions) => string
  axisBottomLegend?: string
  axisLeftLegend?: string
  showPoints?: boolean
  legendItemWidth?: number
  grouped?: boolean
}

const axisLegends: Record<string, string> = {
  day: i18n.t('buildingEnergyModels.calibration.chart.axisLegends.day'),
  week: i18n.t('buildingEnergyModels.calibration.chart.axisLegends.week'),
  year: i18n.t('buildingEnergyModels.calibration.chart.axisLegends.year'),
  outdoorTemperature: i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.outdoorTemperature',
  ),
  power: i18n.t('buildingEnergyModels.calibration.chart.axisLegends.power'),
  heatingPower: i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.heatingPower',
  ),
  heatingEnergy: i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.heatingEnergy',
  ),
  coolingPower: i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.coolingPower',
  ),
  coolingEnergy: i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.coolingEnergy',
  ),
  electricityPower: i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.electricityPower',
  ),
  electricityConsumtion: i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.electricityConsumtion',
  ),
}

const xValueHourFormatter = (value: number) => formatHour(value)
const xValueTemperatureFormatter = (value: number) => formatTemperature(value)

const yValueFixedNumberFormatter = (value: number) => value.toFixed(2)
const yValueToStringFormatter = (value: number) => value.toString()

export const initGraphData = (year: number): GraphData[] => [
  {
    id: 0,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.0'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [EnergySystemType.DomesticHotWater],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: axisLegends.day,
    axisLeftLegend: `${axisLegends.heatingPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 1,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.1'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [EnergySystemType.DomesticHotWater],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: axisLegends.day,
    axisLeftLegend: `${axisLegends.heatingPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 2,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.2'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [EnergySystemType.DomesticHotWater],
    chartType: CalibrationChartType.Bar,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisBottomLegend: axisLegends.year,
    axisLeftLegend: `${axisLegends.heatingEnergy}, ${energyCapacityPerAreaUnit}/${monthUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 3,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.3'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueTemperatureFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: `${axisLegends.outdoorTemperature}, ${temperatureUnit}`,
    axisLeftLegend: `${axisLegends.heatingPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 4,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.4'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: axisLegends.day,
    axisLeftLegend: `${axisLegends.heatingPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 5,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.5'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueTemperatureFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: `${axisLegends.outdoorTemperature}, ${temperatureUnit}`,
    axisLeftLegend: `${axisLegends.heatingPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 6,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.6'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Bar,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisBottomLegend: axisLegends.year,
    axisLeftLegend: `${axisLegends.heatingEnergy}, ${energyCapacityPerAreaUnit}/${monthUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 7,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.7', { year }),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisBottomLegend: axisLegends.year,
    axisLeftLegend: `${axisLegends.power}, ${powerPerAreaUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 8,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.8'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisBottomLegend: axisLegends.week,
    axisLeftLegend: `${axisLegends.power}, ${powerPerAreaUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 9,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.9'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisLeftLegend: `${axisLegends.power}, ${powerPerAreaUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 10,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.10'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: axisLegends.day,
    axisLeftLegend: `${axisLegends.heatingPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 11,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.11'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    legendItemWidth: 150,
  },
  {
    id: 12,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.12'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueTemperatureFormatter,
    yFormatter: yValueToStringFormatter,
  },
  {
    id: 13,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.13'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueTemperatureFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: `${axisLegends.outdoorTemperature}, ${temperatureUnit}`,
    axisLeftLegend: `${axisLegends.coolingPower}, ${powerPerAreaUnit}`,
    showPoints: true,
    legendItemWidth: 150,
  },
  {
    id: 14,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.14'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisLeftLegend: `${axisLegends.coolingPower}, ${powerPerAreaUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 15,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.15'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
    ],
    chartType: CalibrationChartType.Bar,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisBottomLegend: axisLegends.year,
    axisLeftLegend: `${axisLegends.coolingEnergy}, ${energyCapacityPerAreaUnit}/${monthUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 16,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.16'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.ElectricityConsumption,
      EnergySystemType.Ventilation,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Bar,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisBottomLegend: axisLegends.year,
    axisLeftLegend: `${axisLegends.electricityConsumtion}, ${energyCapacityPerAreaUnit}/${monthUnit}`,
  },
  {
    id: 17,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.17'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.ElectricityConsumption,
      EnergySystemType.Ventilation,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueFixedNumberFormatter,
    axisBottomLegend: axisLegends.day,
    axisLeftLegend: `${axisLegends.electricityPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 18,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.18'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.ElectricityConsumption,
      EnergySystemType.Ventilation,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueTemperatureFormatter,
    yFormatter: yValueToStringFormatter,
    axisBottomLegend: `${axisLegends.outdoorTemperature}, ${temperatureUnit}`,
    axisLeftLegend: `${axisLegends.electricityPower}, ${powerPerAreaUnit}`,
  },
  {
    id: 19,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.19'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.ElectricityConsumption,
      EnergySystemType.Ventilation,
      EnergySystemType.HeatLoad,
    ],
    chartType: CalibrationChartType.Line,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisLeftLegend: `${axisLegends.electricityPower}, ${powerPerAreaUnit}`,
    legendItemWidth: 150,
  },
  {
    id: 20,
    name: i18n.t('buildingEnergyModels.calibration.chart.titles.20'),
    measuredData: { id: '', data: [] },
    energySystemData: [],
    dependentEnergySystems: [
      EnergySystemType.DomesticHotWater,
      EnergySystemType.SpaceHeating,
      EnergySystemType.Ventilation,
      EnergySystemType.Cooling,
      EnergySystemType.HeatLoad,
      EnergySystemType.ElectricityConsumption,
    ],
    chartType: CalibrationChartType.Bar,
    xFormatter: xValueHourFormatter,
    yFormatter: yValueToStringFormatter,
    axisLeftLegend: `${energyCapacityPerAreaUnit}`,
    grouped: true,
  },
]
